// The babysit lane's task spec, and the two bounded evidence shapes it carries
// back — files touched, and the seam a change moved. See `../task-envelope.ts`
// for the file docblock.

import { z } from 'zod';

/**
 * The `files_touched` caps, named so {@link provableFilesTouched} enforces the
 * SAME numbers the schema below rejects on. Two spellings of one cap is how a
 * "safe" degrade quietly becomes a parse failure at the mint.
 */
export const FILES_TOUCHED_MAX = 100;
export const FILES_TOUCHED_PATH_MAX = 300;

/** Most `failed_checks` entries one envelope carries — the mint bounds its list to it. */
export const BABYSIT_FAILED_CHECKS_MAX = 100;
/** Longest check name one `failed_checks` entry carries — the mint ellipsizes to it. */
export const BABYSIT_FAILED_CHECK_NAME_MAX = 255;

/** Most review threads one `fix_pr` envelope names, and most verdicts one threads file carries (#3884). */
export const BABYSIT_THREADS_MAX = 200;
/** Longest GraphQL node id a thread reference carries (`PRRT_…` ids run ~20-40 chars). */
export const BABYSIT_THREAD_ID_MAX = 255;
/** Longest reason one thread verdict carries — one or two sentences, posted as the reply. */
export const BABYSIT_THREAD_REASON_MAX = 1_000;

/**
 * Where a `fix_pr` round's agent writes its per-thread verdicts (#3884) — checkout-relative,
 * under the harness scratch root like `BABYSIT_NO_CHANGE_PATH`, named literally by the brief
 * (`apps/worker` `renderBabysitBrief`) and read by the harness (`apps/runner`) after the
 * adapter returns. Here because neither app may import the other.
 */
export const BABYSIT_THREADS_VERDICT_PATH = 'tmp/dz/threads.json' as const;

/** The severities a thread ref may carry — `FindingSeverity` (`entities/babysit/observation.ts`), restated as a schema. */
export const BABYSIT_THREAD_SEVERITIES = ['critical', 'major', 'minor', 'nit'] as const;

/**
 * One of OUR reviewer's UNRESOLVED review threads a `fix_pr` round was minted to answer
 * (#3884) — enough for the harness to reply to it and resolve it without a re-read of the
 * finding.
 *
 * - `thread_id` — the GraphQL node id (`PRRT_…`, what `resolveReviewThread` and
 *   `addPullRequestReviewThreadReply` take).
 * - `comment_id` — the thread's ROOT review comment's REST id, the same number as the
 *   `PrFinding.id` the brief names.
 * - `path` — the file the thread anchors to; null for a pathless thread.
 * - `blocking` — the finding's own `🚫` tag. Our reviewer does not emit it today (0 of
 *   2,657 root comments over the 14 days to 2026-09-13), so it is NOT the gate that keeps
 *   a finding open; `severity` is.
 * - `severity` — the finding's severity as the mint read it off the root comment; null when
 *   the mint could not read one. The box resolves a thread on the model's word alone (a
 *   decline, or a `fixed` git cannot prove) only when it is `!blocking` and `minor` / `nit`:
 *   a `critical` / `major` or an unknown (null) severity is answered and left OPEN, so the
 *   model's own reason cannot close it.
 * - `original_commit_sha` — the full 40-hex commit the root comment was made on (GitHub's
 *   `originalCommit.oid`), null when GitHub reports none. The box uses it to prove a fix
 *   that landed in an EARLIER commit (`git diff <sha> HEAD -- <path>`) instead of reading
 *   the agent's `fixed` claim at its word.
 *
 * `severity` and `original_commit_sha` are REQUIRED (nullable, never absent): a ref minted
 * without them would read as "unknown severity" and be indistinguishable from a mint that
 * looked and found none. Only the `threads` key as a whole is optional, for pre-#3884 rows.
 */
export const BabysitThreadRefSchema = z.object({
  thread_id: z.string().min(1).max(BABYSIT_THREAD_ID_MAX),
  comment_id: z.number().int().positive(),
  path: z.string().min(1).max(4096).nullable(),
  blocking: z.boolean(),
  severity: z.enum(BABYSIT_THREAD_SEVERITIES).nullable(),
  original_commit_sha: z
    .string()
    .regex(/^[0-9a-f]{40}$/, 'a full 40-hex lowercase commit sha')
    .nullable(),
});
export type BabysitThreadRef = z.infer<typeof BabysitThreadRefSchema>;

/**
 * What the agent decided about ONE listed thread (#3884). `fixed` when the finding is
 * addressed at this head, by this round's commit or by code already on the branch;
 * `declined` with the concrete reason otherwise. The harness grades the claim: git's proof
 * on the thread's path resolves any severity, the model's word alone only a `!blocking`
 * `minor` / `nit`, whichever verdict (runner `thread-egress.ts` `modelWordMayClose`) —
 * this schema only bounds the shape.
 */
export const BabysitThreadVerdictEntrySchema = z.object({
  thread_id: z.string().min(1).max(BABYSIT_THREAD_ID_MAX),
  verdict: z.enum(['fixed', 'declined']),
  reason: z.string().trim().min(1).max(BABYSIT_THREAD_REASON_MAX),
});
export type BabysitThreadVerdictEntry = z.infer<typeof BabysitThreadVerdictEntrySchema>;

/** The whole of {@link BABYSIT_THREADS_VERDICT_PATH}: one entry per listed thread, bounded. */
export const BabysitThreadVerdictSchema = z
  .array(BabysitThreadVerdictEntrySchema)
  .max(BABYSIT_THREADS_MAX);
export type BabysitThreadVerdict = z.infer<typeof BabysitThreadVerdictSchema>;

/**
 * Where a piece of PR feedback was read from — `@developerz/domain` `PrCommentSource`
 * (#4260). Each is its own GitHub id space; see `addressed_comments`.
 */
export const PR_COMMENT_SOURCES = ['issue_comment', 'review_body', 'review_thread'] as const;

/**
 * Every action a stored `pr_babysit` envelope may carry. `decideBabysit` mints
 * {@link BabysitMintableAction}; the rest is what the rows already written still say.
 */
export const BABYSIT_SPEC_ACTIONS = [
  'fix_pr',
  'resolve_conflict',
  'address_comments',
  // RETIRED by plan 101 E11 — bot timeline comments no longer mint a follow-up (the
  // decision core never returns it). Kept, like the two below, because stored rows
  // carry it and a stored envelope that no longer parses is a row nobody can read.
  'reply_bot',
  // RETIRED by #3883 — `fix_pr` replaced both. Nothing decides or mints them any more;
  // they stay in the enum only because task rows are forever and a stored envelope
  // that no longer parses is a row nobody can read.
  'fix_ci',
  'address_findings',
] as const;

/** The actions `decideBabysit` can still mint — the spec enum minus the retired three. */
export type BabysitMintableAction = Exclude<
  (typeof BABYSIT_SPEC_ACTIONS)[number],
  'fix_ci' | 'address_findings' | 'reply_bot'
>;

/**
 * The park reasons a stuck AI-managed PR is RESCUED from (#6172, widened by #6231 and
 * again by #6343; `rescueStuckPr`, `entities/babysit/stuck-rescue.ts`): the parks a rescue
 * round exists for. Declared HERE, with the stored spec that carries one, because the
 * decision's vocabulary is derived from the stored enum (as `BabysitMintableAction` is from
 * {@link BABYSIT_SPEC_ACTIONS}) and a rename of a stored value is a new value. Widened
 * ADDITIVELY only: a stored row names any member it ever held.
 *
 * TWO RELEASES, ONE SET (#6343). These members were added READERS ONLY: the stored enum
 * and every read side accept them, and nothing minted them at the time, because a member
 * a lagging box cannot decode is never written in the release that adds it
 * (`packages/db/docs/migration-rules.md` § stored closed sets). #6344 moved
 * `followup_held` and `followup_unrepeatable` into {@link STUCK_PR_RESCUE_MINT_REASONS}
 * by wiring their rounds the release after, #6345 moved `own_review_hold_stale` and
 * `findings_unresolved`, and #6346 moved the last one, `pr_intent_reverted`: every member
 * is now minted.
 */
export const STUCK_PR_RESCUE_REASONS = [
  'merge_conflict',
  'ci_not_converging',
  'ci_fix_declined',
  'findings_not_converging',
  'max_sweeps_exceeded',
  // #6343 readers-only; #6344 wired the first two, #6345 wired `own_review_hold_stale`
  // + `findings_unresolved`, and #6346 wired `pr_intent_reverted` (see the mint set).
  'followup_held',
  'followup_unrepeatable',
  'own_review_hold_stale',
  'findings_unresolved',
  'pr_intent_reverted',
] as const;
export type StuckPrReason = (typeof STUCK_PR_RESCUE_REASONS)[number];

/**
 * The members of {@link STUCK_PR_RESCUE_REASONS} a rescue MINT may still be stamped with:
 * the five #6172/#6231 reasons plus the two #6344 wired — `followup_held` (the held-round
 * refile past a task-owned hold, `entities/babysit/stuck-rescue.ts`) and
 * `followup_unrepeatable` (the unrepeatable round re-minted under the rescue's own id,
 * `apps/worker` `followup-executors.ts`) — plus the two #6345 wired, our own reviewer's
 * holds that still owe a findings round: `own_review_hold_stale` and `findings_unresolved`
 * (both buy a findings-only `fix_pr`; a hold with nothing left to fix returns the
 * escalation MARKED `rescue` so the driver spends ONE fresh re-review on it) — plus the
 * last #6343 member, `pr_intent_reverted`, wired by #6346 (a rescue `fix_pr` whose brief
 * leads with the revert). `rescuable` (`entities/babysit/stuck-rescue.ts`) gates the round
 * mint on THIS set alone, so widening the stored vocabulary cannot make a decision emit a
 * reason this release does not write; a member moves into this set only in the release that
 * wires its round. `isRescuableReason` (`value-objects/stuck-pr.ts`) answers the wider
 * question "does SOME rescue answer this reason" — this set, plus the CI re-run rescue's
 * reasons (`CI_RERUN_RESCUE_REASONS`, a `rerun_checks` hop, never a mint) and our reviewer's
 * re-review reasons (`REVIEW_RESCUE_REASONS`), neither of which a spec ever carries.
 */
export const STUCK_PR_RESCUE_MINT_REASONS = [
  'merge_conflict',
  'ci_not_converging',
  'ci_fix_declined',
  'findings_not_converging',
  'max_sweeps_exceeded',
  // #6344: the held-round refile and the unrepeatable round's rescue id.
  'followup_held',
  'followup_unrepeatable',
  // #6345: our own reviewer's holds that still owe a findings round — both buy a
  // findings-only `fix_pr` (`roundFor`, `entities/babysit/stuck-rescue.ts`).
  'own_review_hold_stale',
  'findings_unresolved',
  // #6346: one rescue `fix_pr` for a round the harness refused for undoing the PR.
  'pr_intent_reverted',
] as const satisfies readonly StuckPrReason[];
export type StuckPrMintReason = (typeof STUCK_PR_RESCUE_MINT_REASONS)[number];

/**
 * The follow-up actions a rescue of each reason may ride — the round the reason's ladder ran
 * out of. `max_sweeps_exceeded` names the excused-round or sweep ceiling of three ladders
 * (CI, findings, conflict; the escalation's `ladder` says which), so it rides either action
 * those ladders mint.
 *
 * The #6343 members: a `followup_held` / `followup_unrepeatable` rescue REFILES the very
 * round the park named (#6338's `refile_held` / `unrepeatable_fix`), so it rides whichever
 * mintable action that round carried; the three reviewer-facing parks ride a `fix_pr`
 * round (`RESCUE_ROUND_OF`'s `findings_fix` / `intent_revert_fix`).
 */
export const STUCK_PR_RESCUE_ACTIONS = {
  merge_conflict: ['resolve_conflict'],
  ci_not_converging: ['fix_pr'],
  ci_fix_declined: ['fix_pr'],
  findings_not_converging: ['fix_pr'],
  max_sweeps_exceeded: ['fix_pr', 'resolve_conflict'],
  followup_held: ['fix_pr', 'resolve_conflict', 'address_comments'],
  followup_unrepeatable: ['fix_pr', 'resolve_conflict', 'address_comments'],
  own_review_hold_stale: ['fix_pr'],
  findings_unresolved: ['fix_pr'],
  pr_intent_reverted: ['fix_pr'],
} as const satisfies Record<StuckPrReason, readonly BabysitMintableAction[]>;

/** Does a rescue of `reason` ride a spec of `action`? See {@link STUCK_PR_RESCUE_ACTIONS}. */
function rescueRides(reason: StuckPrReason, action: string): boolean {
  return (STUCK_PR_RESCUE_ACTIONS[reason] as readonly string[]).includes(action);
}

/**
 * A rescue round's mark on its spec (#6172): which park it rescues and how long the PR's
 * head had gone without progress when the round was minted. `parked_ms` keeps the name it
 * was stored under: until #6231 it was the park's age (from the first escalation row for the
 * reason); since then it is the head's idle time (`BabysitBookkeeping.watchIdleMs`), the one
 * stuck clock. A stored field is never renamed. The model escalation ladder reads the mark's
 * PRESENCE (`apps/worker` `dispatch/model-escalation.ts`: the claim escalates from its first
 * attempt, trigger `stuck_pr`), the brief states it, and babysit's bookkeeping reads it back
 * off the stored rows as "this reason was already rescued since the re-arm".
 */
export const BabysitRescueSpecSchema = z.object({
  reason: z.enum(STUCK_PR_RESCUE_REASONS),
  parked_ms: z.number().int().nonnegative(),
});
export type BabysitRescueSpec = z.infer<typeof BabysitRescueSpecSchema>;

/**
 * PR-BABYSIT sweep context (#543, epic #541). Present ONLY when
 * `origin === 'pr_babysit'`: a fleet-opened PR needs one driven action toward a
 * merge. `head_sha` pins the exact commit the sweep observed (idempotency key —
 * a re-push mints a fresh sweep); `action` is the single prescribed step from the
 * pure babysit decision (`@developerz/domain` `decideOutcome`), and `attempt`
 * bounds the CI self-fix retry loop (1..3, `DEFAULT_MAX_CI_FIX_ATTEMPTS`; uncapped on an
 * AI-managed PR, whose CI ladder the convergence stop bounds).
 * Schema-only until the babysit dispatch slice (B1b) wires the producer.
 */
export const BabysitTaskSpecSchema = z
  .object({
    pr_number: z.number().int().positive(),
    head_sha: z.string().min(7).max(64),
    /**
     * The base branch's tip commit AT THE OBSERVATION this follow-up was minted from
     * (#4289) — distinct from `base_ref`, which is the branch NAME and never changes
     * when main moves. This is the other half of the conflict-resolve budget's key: a
     * `resolve_conflict` follow-up is keyed on the (`head_sha`, `base_sha`) PAIR, so a
     * base that moved (main merged again) after a resolved conflict re-conflicts a
     * DIFFERENT pair and earns a fresh budget, where the PR-lifetime count it replaced
     * kept charging a conflict that had already been fixed once.
     *
     * OPTIONAL, and permanently so for rows minted before this field existed — a
     * stored envelope that no longer parses is a row nobody can read. Every mint since
     * #4289 stamps it; `conflictAttemptCounts` (`@developerz/domain`
     * `entities/babysit/policy.ts`) treats an absent value as a MATCH (fails closed
     * toward the pre-#4289 PR-lifetime count) so the legacy population is never
     * under-counted — only a `base_sha` actually on the row may exclude it, and the
     * legacy population drains on its own as PRs get new heads.
     */
    base_sha: z.string().min(7).max(64).optional(),
    base_ref: z.string().min(1).max(255),
    head_ref: z.string().min(1).max(255),
    action: z.enum(BABYSIT_SPEC_ACTIONS),
    // No upper bound HERE (#4289): `resolve_conflict`'s `attempt` is an ID
    // DISCRIMINATOR (the per-head mint rung, `BabysitBookkeeping.
    // conflictResolveMintAttempts + 1`), not a budget — the budget is
    // `maxConflictResolveAttempts` on the charged count, judged once in
    // `decideMergeGate` and reasserted once in `overAttemptCeiling`
    // (`apps/worker/src/babysit/followup-executors.ts`). A base can move an
    // unbounded number of times over one PR's life (`decideMergeGate`'s conflict
    // branch has no `max_sweeps` check), so a static max here would throw
    // `TaskEnvelopeSchema.parse` on a legitimate retry — #4289's own defect class
    // reappearing at a further-out number. The `superRefine` below is where the
    // real per-action bound lives: it holds every OTHER action to 3, because
    // THEIR `attempt` genuinely is their own budget count — a CI `fix_pr` round excepted
    // (an AI-managed PR's CI ladder has no count budget, owner ruling 2026-10-09).
    attempt: z.number().int().min(1),
    /**
     * The checks that FAILED on the settled head a `fix_pr` round was minted for
     * (#3883) — check-run names and commit-status contexts alike, because the observe
     * seam folds both into one graded set. Present on `fix_pr` only, and `[]` there when
     * the round carries findings alone: the empty list is the fact "CI was green", which
     * an absent key could not state. The box stages the failed logs when it is non-empty.
     */
    failed_checks: z
      .array(z.string().min(1).max(BABYSIT_FAILED_CHECK_NAME_MAX))
      .max(BABYSIT_FAILED_CHECKS_MAX)
      .optional(),
    /**
     * OUR reviewer's UNRESOLVED review threads this `fix_pr` round answers (#3884) — one
     * {@link BabysitThreadRefSchema} per finding the round carries, so the harness can
     * reply to each and resolve the ones it fixed off {@link BABYSIT_THREADS_VERDICT_PATH}.
     *
     * Every `fix_pr` mint since #3884 stamps it — `[]` on a CI-only round. It is optional
     * ONLY because `fix_pr` rows minted between #3883's deploy and #3884's carry none, and
     * a stored envelope that no longer parses is a row nobody can read; an absent key is
     * such a row, never a new mint. Absent for every other action.
     */
    threads: z.array(BabysitThreadRefSchema).max(BABYSIT_THREADS_MAX).optional(),
    /**
     * The HUMAN feedback this `address_comments` follow-up handled (#581), each as its
     * `(source, id)` identity (#4260). The driver's bookkeeping reconstructs the
     * cross-sweep address-once set from prior follow-ups, so handled feedback drops out
     * of the next sweep instead of pinning the PR at `address_comments` forever. Absent
     * for the other actions.
     *
     * Replaced `addressed_comment_ids` (bare numbers) when review bodies and review
     * threads became feedback too: an issue comment id, a review id and a review-comment
     * id are three GitHub id spaces, and a bare number from one retired a different
     * comment in another. No decoder for the old key — a stored row simply stops
     * retiring (prod at the change: 0 non-terminal rows carried it), so at worst a
     * comment is addressed once more.
     */
    addressed_comments: z
      .array(z.object({ source: z.enum(PR_COMMENT_SOURCES), id: z.number().int() }))
      .max(200)
      .optional(),
    /**
     * STORED ROWS ONLY since plan 101 E11 retired `reply_bot` — kept so an envelope
     * written before then still parses.
     *
     * The BOT comment ids this `reply_bot` follow-up acknowledged — always
     * `issue_comment` ids, the only source a bot's feedback is read from. The twin of
     * `addressed_comments` above, and it exists for the same reason — except the
     * bug it fixes was not "park forever", it was **never merge**.
     *
     * `decideBabysit` returns `reply_bot` from a branch ABOVE the CI/merge branches,
     * and it built its candidate list from a `repliedCommentIds` set the worker
     * hard-coded EMPTY. So a bot comment was permanently unacknowledged: the driver
     * decided `reply_bot`, the follow-up deduped on its deterministic id
     * (`followup_deduped` — same head sha, same id), nothing changed, and the next
     * sweep decided `reply_bot` again. The dedup correctly stopped double WORK; what
     * nothing stopped was the state machine being pinned one branch above `merge`.
     * On a repo where a review bot comments on every PR, that is every PR.
     *
     * Absent for the other actions.
     */
    replied_comment_ids: z.array(z.number().int()).max(200).optional(),
    /**
     * The paths this round took BEYOND its PR's diff through `files_widen`
     * (`widenHeldTaskFiles`, `@developerz/db`) — the entries of `files_touched` that are
     * not the PR's own changed files. The #513 gate holds another writer behind a
     * box-held round by THESE only: an overlap with the PR's diff exists whether or not a
     * round is running, and holding the writer for the round's duration cannot un-make it
     * (`areaHoldKindSql`'s babysit paragraphs).
     *
     * Every mint stamps `[]`. OPTIONAL only because rows minted before the field carry
     * none, and a stored envelope that no longer parses is a row nobody can read; such a
     * row keeps the ordinary file-set proof on its whole set, failing closed, because its
     * `files_touched` may already hold a widening nobody recorded.
     */
    widened_paths: z
      .array(z.string().min(1).max(FILES_TOUCHED_PATH_MAX))
      .max(FILES_TOUCHED_MAX)
      .optional(),
    /**
     * The dependency bot that opened the PR (`isDependencyBotLogin`, `@developerz/review`),
     * stamped by the mothership's one mint site (`apps/worker` `followup-executors.ts`);
     * absent for every other author. The box's land refuses a fix round that sets every
     * version the bot bumped back to the base's (`apps/runner` `pr-intent.ts`, #6097).
     * OPTIONAL both ways: a row minted before the field, or a PR a person opened, carries
     * none, and that is the "not a dependency bot" answer the guard reads.
     */
    dependency_bot_author: z.string().min(1).max(64).optional(),
    /**
     * Present ONLY on a stuck-PR RESCUE round (#6172, {@link BabysitRescueSpecSchema}): the
     * one extra round an AI-managed PR whose head made no progress for 12h+ gets when it
     * escalates a fixable park, on the escalation agent's stronger model. Absent on every ordinary round. Declared because
     * this schema strips an undeclared key, and the stored mark is what makes the rescue
     * once per reason: the bookkeeping reads it back off the PR's prior rows.
     */
    rescue: BabysitRescueSpecSchema.optional(),
    /**
     * STORED ROWS ONLY since #3884 — no mint stamps it and nothing reads it; kept so an
     * envelope written before then still parses.
     *
     * It carried the review-comment ids a `fix_pr` / `address_findings` round was minted
     * for (#2017), which the worker's bookkeeping unioned into a retire set: the REST
     * comment read carried no resolution, so a fixed finding kept reappearing
     * (`developerz-ai/developerz.ai#1783`, 10 tasks across 10 heads). Findings are
     * unresolved review THREADS now, and {@link BabysitThreadRefSchema} on `threads`
     * carries the same `comment_id` beside the thread id.
     */
    addressed_finding_ids: z.array(z.number().int()).max(200).optional(),
    /**
     * STORED ROWS ONLY since #3884, like `addressed_finding_ids` — the refuted twin the mint
     * stamped with the same ids (#3102). A declined finding is answered on its thread now
     * (`verdict: 'declined'` in {@link BABYSIT_THREADS_VERDICT_PATH}).
     */
    refuted_finding_ids: z.array(z.number().int()).max(200).optional(),
  })
  .superRefine((spec, ctx) => {
    // `failed_checks` is REQUIRED on `fix_pr` — present, possibly `[]` (PR #3887 review).
    // `[]` is the fact "this round carries findings only"; ABSENT would leave the box to
    // guess whether CI failed, so it is refused at parse rather than at claim time. The
    // retired actions predate the field, so their stored rows keep parsing without it.
    if (spec.action === 'fix_pr' && spec.failed_checks === undefined) {
      ctx.addIssue({
        code: 'custom',
        path: ['failed_checks'],
        message: 'a fix_pr spec must carry failed_checks ([] for a findings-only round)',
      });
    }
    // THIS is where `attempt`'s per-action bound actually lives (#4289). The base
    // schema deliberately declines to bound it (`.min(1)`, no `.max()`) because
    // `resolve_conflict`'s `attempt` is an ID DISCRIMINATOR (the per-head mint rung),
    // not a budget — a static schema max on an id would throw on a legitimate retry
    // the moment a long-lived PR outlived it, which is #4289's own defect class
    // recurring at a further-out number. Every OTHER action's `attempt` genuinely IS
    // its own budget count (bounded by `DEFAULT_MAX_FEEDBACK_ATTEMPTS`, 3), so this refuses
    // those past 3 — a bug elsewhere still fails loudly rather than silently accepting an
    // unbounded value. A CI `fix_pr` (one carrying a failed check) is uncapped too: on an
    // AI-managed PR its ladder has no count budget (owner ruling 2026-10-09, `aiManagedPr`),
    // the decision's convergence stop bounds it, and every other PR is held to
    // `maxCiFixAttempts` by the decision and the mint's `overAttemptCeiling`. A RESCUE round
    // (#6231) is uncapped too: it exists because its ladder ran out, so a findings-only
    // rescue is past 3 by construction, and its own bound is once per (PR, reason).
    const ciRound = spec.action === 'fix_pr' && (spec.failed_checks ?? []).length > 0;
    const uncapped = spec.action === 'resolve_conflict' || ciRound || spec.rescue !== undefined;
    if (!uncapped && spec.attempt > 3) {
      ctx.addIssue({
        code: 'custom',
        path: ['attempt'],
        message: `attempt must be <= 3 for action '${spec.action}' (only resolve_conflict's attempt, a CI fix_pr round's and a rescue round's are uncapped)`,
      });
    }
    // A rescue rides only the round its reason's ladder runs (#6172): a `fix_pr` marked as a
    // conflict rescue would be read back as "the conflict was rescued" with no conflict
    // round ever minted. Refused at the mint's parse, like `failed_checks` above.
    if (spec.rescue !== undefined && !rescueRides(spec.rescue.reason, spec.action)) {
      ctx.addIssue({
        code: 'custom',
        path: ['rescue'],
        message: `a ${spec.rescue.reason} rescue rides a ${STUCK_PR_RESCUE_ACTIONS[spec.rescue.reason].join(' or ')} spec, not '${spec.action}'`,
      });
    }
    // One ref per thread (#3889 review). The box replies once per listed ref, so a thread
    // listed twice would be answered twice; refused at the mint's parse, never deduped here.
    const seen = new Set<string>();
    const repeated = new Set<string>();
    for (const thread of spec.threads ?? []) {
      if (seen.has(thread.thread_id)) repeated.add(thread.thread_id);
      seen.add(thread.thread_id);
    }
    if (repeated.size > 0) {
      ctx.addIssue({
        code: 'custom',
        path: ['threads'],
        message: `threads must name each thread once; repeated: ${[...repeated].join(', ')}`,
      });
    }
  });
export type BabysitTaskSpec = z.infer<typeof BabysitTaskSpecSchema>;

/**
 * Turn an OBSERVED path list into one that may be DECLARED as `files_touched`
 * — or into nothing at all, which is the answer far more often than it looks.
 *
 * ## Why this exists
 *
 * Measured on a customer account 2026-08-23: **554 of 554 tasks ever created
 * declared `files_touched: []`** (552 of them from the PR lanes — 335
 * `pr_review_reply`, 105 `pr_review`, 56 `pr_babysit`), and none was
 * `parallel_safe`. Both locks on concurrency were therefore shut at once: the
 * #513 area gate serialises per `(account, repo)` by default, and the #2278
 * file-set escape (`provablyDisjointSql`) can never fire because it requires a
 * non-empty set on BOTH sides. The account had 3 seats, 3 online boxes and a
 * 3-slot entitlement; peak observed concurrency was 1, throughput 1-4 tasks/hour,
 * and the queue reached 89. The escape has simply never had inputs.
 *
 * A PR's own changed-file list is an input it can have: knowable, cheap, and
 * authoritative (`listPullRequestFiles`, `@developerz/github`).
 *
 * ## Why every failure degrades to `[]` rather than to a shorter list
 *
 * A declared set is a CONCURRENCY CONTRACT, not a hint — it is the entire basis
 * on which two boxes are allowed onto one repo, and the coding brief tells the
 * agent the set is exclusive (`EXCLUSIVE_FILE_SET_CONTRACT`,
 * `apps/runner/src/harness/prompt.ts`). So the only two honest answers are the
 * WHOLE list or none of it:
 *
 *   - `null` — the read faulted or ran past its page budget
 *     (`PR_FILES_MAX_PAGES`). An unread page may hold the one file that
 *     collides, so an incomplete list is not a proof of anything.
 *   - MORE than {@link FILES_TOUCHED_MAX} paths — slicing to the cap would
 *     declare disjointness on behalf of the files silently dropped. That is the
 *     one failure mode that actually breaks a repo: two branches edit one file,
 *     both PRs go green alone, and the second merge quietly reverts the first.
 *   - a path longer than {@link FILES_TOUCHED_PATH_MAX} (or blank) — dropping
 *     just that path is the same lie in miniature, and keeping it would fail
 *     `TaskEnvelopeSchema.parse` and cost the enqueue entirely.
 *
 * `[]` is never a degraded ANSWER, it is the absence of one: the gate reads an
 * unscoped task as an unknown blast radius and serialises the repo behind it —
 * exactly today's behaviour, which is what makes every branch here safe to take.
 *
 * De-dupe runs BEFORE the cap on purpose: collapsing a repeated path shortens
 * the list without dropping anything, so a list that fits only after de-duping
 * is still the complete set.
 *
 * Pure.
 */
export function provableFilesTouched(paths: readonly string[] | null | undefined): string[] {
  if (paths == null) return [];
  const unique = [...new Set(paths)];
  if (unique.length === 0 || unique.length > FILES_TOUCHED_MAX) return [];
  for (const path of unique) {
    if (path.length === 0 || path.length > FILES_TOUCHED_PATH_MAX) return [];
  }
  return unique;
}

/**
 * A `files_touched` entry that names a SUBTREE rather than a file: `apps/frontend/` covers
 * every path beneath it. The encoding is the trailing `/`, and it is unambiguous because a
 * changed-file path (git's, GitHub's `pulls/:n/files`) never ends in one.
 *
 * NOTHING NEW HAS TO LEARN IT. Every reader already compares declared paths by SEGMENT
 * PREFIX after stripping trailing slashes — the area gate (`provablyDisjointSql`,
 * `@developerz/db`, both directions), the claim's re-check (the same fragment), and the
 * planner's `pathsOverlap` (`@developerz/agent`) — so `apps/frontend/` overlaps
 * `apps/frontend/src/a.ts` and `apps/` there already. The coding brief tells the agent a
 * new file inside a listed directory is inside its set (`EXCLUSIVE_FILE_SET_CONTRACT`,
 * `apps/runner`). This predicate exists for the producer and for tests.
 */
export function isSubtreeEntry(entry: string): boolean {
  return entry.endsWith('/');
}

/**
 * Does this entry normalize to NOTHING — whitespace, `/`, `./`? The readers strip
 * surrounding whitespace, a leading `./` and trailing slashes, then SKIP an empty path, so
 * such an entry is a non-empty declaration that covers nothing: provably disjoint from
 * every sibling, which is the one fail-open this field can have. The envelope refuses it.
 */
export function coversNothing(entry: string): boolean {
  return entry.trim().replace(/^\.\//, '').replace(/\/+$/, '') === '';
}

/**
 * `a/b/c.ts` → `a/b/`, `a/b/` → `a/`, a top-level entry → `null` (its parent is the root).
 * A prefix that {@link coversNothing} (`./` of `./a.ts`, ` /` of ` /a.ts`) IS the root, so
 * it is `null` too (#7601): emitted, it was an entry `files_touched` refuses, and a reader
 * that skips it reads the declaration as provably disjoint from everything.
 */
function parentPrefix(entry: string): string | null {
  const trimmed = entry.endsWith('/') ? entry.slice(0, -1) : entry;
  const cut = trimmed.lastIndexOf('/');
  if (cut <= 0) return null;
  const prefix = `${trimmed.slice(0, cut)}/`;
  return coversNothing(prefix) ? null : prefix;
}

/** Segments above an entry — the depth of its parent prefix. */
function parentDepth(entry: string): number {
  const parent = parentPrefix(entry);
  return parent === null ? 0 : parent.split('/').length - 1;
}

/**
 * {@link provableFilesTouched}, except that a set over {@link FILES_TOUCHED_MAX} (or with
 * a path over {@link FILES_TOUCHED_PATH_MAX}) is COARSENED into directory prefixes that
 * still cover every path — never truncated, and emptied only when nothing smaller than the
 * whole repository covers it.
 *
 * ## Why
 *
 * `[]` reads as an unknown blast radius and serialises the repo behind the task for its
 * whole run. tesote/tesote.ai (2026-09-18): the babysit fix round on #2452 (106 changed
 * files at the time) declared `[]` and held three small, declared editing tasks
 * `area_busy` for 30+ minutes with 11 boxes idle — and 80–100+ file PRs are that repo's
 * norm. A covering prefix set is as SAFE as the full list (nothing is dropped: every path
 * is under some entry) and admits every sibling outside those subtrees.
 *
 * ## How
 *
 * Deepest first, widest first: while the set is over the cap, take the deepest parent
 * directory any entry has, and among those the one holding the most entries (ties by
 * name), and replace its entries with the one subtree entry. That keeps every entry as
 * specific as the cap allows. Over-long paths are lifted to their deepest ancestor that
 * fits first. When every entry is already top-level and the set is still over the cap,
 * only the whole repository covers it — which is `[]`.
 *
 * `null` (a faulted or incomplete read), an empty list, or a blank path still answer `[]`:
 * an incomplete list is not a proof, and coarsening cannot recover a path nobody read.
 *
 * Pure.
 */
export function coveringFilesTouched(paths: readonly string[] | null | undefined): string[] {
  if (paths == null || paths.length === 0) return [];
  if (paths.some((path) => coversNothing(path))) return [];
  let entries = new Set<string>();
  for (const path of paths) {
    let entry: string | null = path;
    while (entry !== null && entry.length > FILES_TOUCHED_PATH_MAX) entry = parentPrefix(entry);
    if (entry === null) return [];
    entries.add(entry);
  }
  entries = dropCovered(entries);
  while (entries.size > FILES_TOUCHED_MAX) {
    const deepest = Math.max(...[...entries].map(parentDepth));
    if (deepest === 0) return [];
    const groups = new Map<string, string[]>();
    for (const entry of entries) {
      if (parentDepth(entry) !== deepest) continue;
      const parent = parentPrefix(entry) as string;
      groups.set(parent, [...(groups.get(parent) ?? []), entry]);
    }
    const [parent, members] = [...groups.entries()].sort(
      ([a, x], [b, y]) => y.length - x.length || a.localeCompare(b),
    )[0] as [string, string[]];
    for (const member of members) entries.delete(member);
    entries.add(parent);
    entries = dropCovered(entries);
  }
  return [...entries].sort();
}

/** Remove every entry a subtree entry in the same set already covers. */
function dropCovered(entries: Set<string>): Set<string> {
  const subtrees = [...entries].filter(isSubtreeEntry);
  return new Set(
    [...entries].filter(
      (entry) => !subtrees.some((tree) => tree !== entry && entry.startsWith(tree)),
    ),
  );
}

/** Longest declared seam symbol — same bound `manifest.ts`'s `SEAM_CHANGE_MAX_LENGTH` uses for its per-file `seam.change` line, so a coordinator's own seam sentence and a task's fit the same room. */
export const SEAM_CHANGE_SYMBOL_MAX = 300;

/**
 * ONE symbol a task changes, and the files ELSEWHERE that read it — the CROSS-RUN
 * half of `hive.md` rule 4 ("the seam is the API, not the path").
 *
 * ## Why this exists
 *
 * `files_touched` plus the #513 area gate's proof-of-disjointness escape
 * (`provablyDisjointSql`, `@developerz/db`) already lets two box-held tasks on one
 * repo run at once when their declared file sets do not overlap. That proof answers
 * "do these tasks EDIT the same file", and disjoint file sets are necessary but not
 * sufficient: a task that renames an export in `a.ts` and a sibling task that edits
 * `b.ts` pass the gate clean even when `b.ts` CALLS the export being renamed —
 * `b.ts`'s edit lands against a signature that no longer exists the moment the first
 * task's PR merges, and neither box ever saw the other's diff to know.
 *
 * `hive.md` already solved this WITHIN one coordinator's run: the team fan-out's
 * file manifest carries `seam: { change, readers }` per declared file
 * (`apps/runner/src/modes/worker/team/manifest.ts`), and refuses a reader that is
 * not declared or sits under a different editor's parallel group. What was missing
 * is the same discipline ACROSS runs — a scout-authored task envelope declared
 * `files_touched` and nothing about the contracts it moves, so two SEPARATE tasks
 * the same-area gate lets through could still break each other's callers with no
 * mechanism to catch it.
 *
 * ## What it is, and is not
 *
 * A declaration, not a lock on its own: `readers` need not be paths this task itself
 * declares in `files_touched` — most often they belong to a DIFFERENT task's set
 * entirely, which is exactly the case this exists for. It is read by the area gate
 * (`provablyDisjointSql`) as ADDITIONAL evidence a sibling task's file set may not
 * overlap: a sibling that declares one of these `readers` in ITS `files_touched` is
 * no longer provably disjoint from this task, whatever the raw path comparison says.
 *
 * Optional, and absent is a clean skip — every producer that has not been taught to
 * populate it keeps exactly today's file-set-only proof, which is the safe fallback:
 * absence of a declared seam is not proof that changing this task's files is safe,
 * it is simply no additional information, same as an empty `files_touched`.
 *
 * `symbol` is free text (an export name, a route, a column) — there is no registry
 * of "kinds of symbol" to keep in step, the same reasoning `manifest.ts`'s own
 * `seam.change` line takes: a human/model-readable name of what changed is enough
 * for the gate, which only ever reads `readers`.
 */
export const SeamChangeSchema = z.object({
  symbol: z.string().trim().min(1).max(SEAM_CHANGE_SYMBOL_MAX),
  readers: z.array(z.string().min(1).max(FILES_TOUCHED_PATH_MAX)).max(FILES_TOUCHED_MAX),
});
export type SeamChange = z.infer<typeof SeamChangeSchema>;
